import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { TenantConfig } from '../types/tenant';
import { ProductType } from '../types/product';
import { tenantAPI } from '../services/api';
import api from '../services/api';
import { facebookPixel } from '../utils/facebookPixel';
import { tenantStorage } from '../utils/storage';
import { getEmbeddedStorefrontConfigSync } from '../utils/storefrontConfig';

interface TenantContextType {
  tenant: TenantConfig | null;
  loading: boolean;
  error: string | null;
  // NEW: Template helper functions
  isProductTypeAllowed: (productType: ProductType) => boolean;
  getAllowedProductTypes: () => ProductType[];
  isPageEnabled: (page: string) => boolean;
  isFeatureEnabled: (feature: string) => boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
};

// Helper function to check if product type is allowed
const checkProductTypeAllowed = (
  tenant: TenantConfig | null,
  productType: ProductType
): boolean => {
  if (!tenant?.template?.allowedProductTypes) {
    // If no template config, allow all types (backward compatibility)
    return true;
  }
  return tenant.template.allowedProductTypes.includes(productType);
};

// Helper function to get all allowed product types
const getAllowedTypes = (tenant: TenantConfig | null): ProductType[] => {
  if (!tenant?.template?.allowedProductTypes) {
    // If no template config, return all types
    return Object.values(ProductType);
  }
  return tenant.template.allowedProductTypes;
};

// Helper function to check if page is enabled
const checkPageEnabled = (tenant: TenantConfig | null, page: string): boolean => {
  if (!tenant?.template?.enabledPages) {
    return true; // Backward compatibility
  }
  return (tenant.template.enabledPages as any)[page] === true;
};

// Helper function to check if feature is enabled
const checkFeatureEnabled = (tenant: TenantConfig | null, feature: string): boolean => {
  if (!tenant?.template?.features) {
    return true; // Backward compatibility
  }
  return (tenant.template.features as any)[feature] === true;
};

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Single mount effect: load tenant config AND set up the preview-overlay
  // postMessage listener. Previously these lived in two parallel useEffects
  // which fought over storefront config — on a fast preview message arriving
  // before the API resolved, the config was fetched twice and the second
  // fetch could overwrite the freshly-merged overlay.
  //
  // An AbortController is shared by both branches so an unmount (e.g. route
  // change before initial load) cancels everything cleanly.
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    let unmounted = false;

    const isPreview =
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).has('__diviPreview');

    const applyOverlay = (payload: any) => {
      if (unmounted) return;
      if (!payload || typeof payload !== 'object') return;
      setTenant((prev) => {
        const base: any = prev || {
          id: payload.id,
          name: payload.name || payload.branding?.companyName || 'Preview',
          branding: {
            companyName: payload.branding?.companyName || payload.name || 'Preview',
            primaryColor: payload.branding?.primaryColor || '#111111',
            secondaryColor: payload.branding?.secondaryColor || '#888888',
          },
        };
        const next: any = {
          ...base,
          name: payload.name ?? base.name,
          branding: {
            ...(base.branding || {}),
            ...(payload.branding || {}),
          },
        };
        if (payload.template)
          next.template = { ...(base.template || {}), ...payload.template };
        if (payload.homepage)
          next.homepage = { ...(base.homepage || {}), ...payload.homepage };
        applyBranding(next as TenantConfig);
        return next;
      });
    };

    const previewHandler = (e: Event) => {
      if (signal.aborted) return;
      const detail =
        (e as CustomEvent).detail ?? (window as any).__STOREFRONT_CONFIG__;
      applyOverlay(detail);
    };

    if (isPreview) {
      window.addEventListener('divi:preview-config', previewHandler as EventListener);
      if ((window as any).__STOREFRONT_CONFIG__) {
        applyOverlay((window as any).__STOREFRONT_CONFIG__);
      }
    }

    // Kick off the tenant load. loadTenantConfig is async and uses the
    // class repos / API client — its in-flight fetches don't accept a
    // signal, but the unmounted flag prevents setState after teardown.
    loadTenantConfig();

    return () => {
      unmounted = true;
      controller.abort();
      if (isPreview) {
        window.removeEventListener(
          'divi:preview-config',
          previewHandler as EventListener,
        );
      }
    };
  }, []);

  // Bind tenant-scoped localStorage as soon as the tenant resolves. This is
  // what makes cart/affiliate/visitorId/checkout_form_data isolated per
  // tenant. Keys written before resolution land in the `_pending` scope and
  // are migrated automatically on the first setTenant call.
  useEffect(() => {
    if (!tenant) return;
    const tenantId =
      (tenant as any).uuid != null
        ? String((tenant as any).uuid)
        : tenant.id != null
        ? String(tenant.id)
        : null;
    tenantStorage.setTenant(tenantId);
  }, [tenant]);

  // Listen for route changes to re-apply branding
  useEffect(() => {
    const handleBrandingUpdate = () => {
      if (tenant) {
        applyBranding(tenant);
      }
    };

    // Listen for custom event
    window.addEventListener('route-changed', handleBrandingUpdate);

    return () => {
      window.removeEventListener('route-changed', handleBrandingUpdate);
    };
  }, [tenant]);

  // (Preview overlay listener moved into the single mount effect above so
  // tenant load and preview merge share one AbortController + lifecycle.)

  const applyBranding = async (config: TenantConfig) => {
    const root = document.documentElement;

    // First, try to get storefront colors (if published).
    // The cartBg* triplet feeds the body gradient via BaseTemplate
    // (lines 841–855). Defaults used to be dark navy regardless of the
    // merchant's palette, which painted the storefront's body dark even when
    // the merchant picked a light brand color. Default these to the merchant's
    // own background so a non-customised cart bg silently matches the rest of
    // the storefront.
    let primaryColor = config.branding.primaryColor;
    let secondaryColor = config.branding.secondaryColor;
    let cartBg0 = '#FFFFFF';
    let cartBg1 = '#FFFFFF';
    let cartBg2 = '#FFFFFF';

    // Detect funnel context only for /f/:slug routes
    const pathname = window.location.pathname;
    const funnelMatch = pathname.match(/^\/f\/([^\/]+)/);
    const funnelSlug = funnelMatch ? funnelMatch[1] : undefined;

    // Fetch storefront config ONCE for this branding pass. Was previously
    // fetched twice in two separate try/catch blocks (colors first, then
    // header colors + websiteId), firing the same network request every
    // time applyBranding ran. Single fetch + single error path.
    let storefrontConfig: any = null;
    try {
      // Import dynamically to avoid circular dependency.
      const { getStorefrontConfig } = await import('../utils/storefrontConfig');
      storefrontConfig = await getStorefrontConfig(undefined, funnelSlug);
    } catch {
      // Storefront not available — fall through with defaults / tenant branding.
      storefrontConfig = null;
    }

    if (storefrontConfig && storefrontConfig.tenantId && storefrontConfig.customColors) {
      const customColors = storefrontConfig.customColors;
      // Storefront colors override tenant branding.
      primaryColor = customColors.primary || primaryColor;
      secondaryColor = customColors.secondary || secondaryColor;
      // Cart background falls back to the storefront's general background
      // so a merchant who only picked a brand color in the wizard still
      // gets a body gradient that matches the rest of their site.
      const fallbackBg = customColors.background || cartBg0;
      cartBg0 = customColors.cartBg0 || fallbackBg;
      cartBg1 = customColors.cartBg1 || fallbackBg;
      cartBg2 = customColors.cartBg2 || fallbackBg;
      console.debug('[TenantContext] storefront colors', {
        funnelSlug,
        primaryColor,
        secondaryColor,
        cartBg0,
        cartBg1,
        cartBg2,
      });
    }

    // Apply CSS variables (use storefront colors if available, otherwise tenant branding)
    if (primaryColor) {
      root.style.setProperty('--primary-color', primaryColor);
      root.style.setProperty('--color-primary', primaryColor); // For BaseTemplate compatibility
    }
    if (secondaryColor) {
      root.style.setProperty('--secondary-color', secondaryColor);
      root.style.setProperty('--color-secondary', secondaryColor); // For BaseTemplate compatibility
    }

    // Apply cart background colors globally (both naming conventions for compatibility)
    root.style.setProperty('--cart-bg-0', cartBg0);
    root.style.setProperty('--cart-bg-1', cartBg1);
    root.style.setProperty('--cart-bg-2', cartBg2);
    root.style.setProperty('--bg-0', cartBg0);
    root.style.setProperty('--bg-1', cartBg1);
    root.style.setProperty('--bg-2', cartBg2);

    // Apply gradient background to html and body for all pages
    const html = document.documentElement;
    const body = document.body;
    const gradient = `linear-gradient(180deg, ${cartBg0}, ${cartBg1} 55%, ${cartBg2})`;

    // Apply to both html and body to ensure it shows
    html.style.background = gradient;
    html.style.backgroundAttachment = 'fixed';
    html.style.minHeight = '100vh';

    body.style.background = gradient;
    body.style.backgroundAttachment = 'fixed';
    body.style.minHeight = '100vh';

    // Header colors + websiteId derive from the SAME storefrontConfig we
    // already fetched above — no second network call.
    if (storefrontConfig && storefrontConfig.tenantId && storefrontConfig.customColors) {
      const customColors = storefrontConfig.customColors;
      if (customColors.headerBg) {
        root.style.setProperty('--glass-bg', customColors.headerBg);
      }
      if (customColors.headerText) {
        root.style.setProperty('--header-text-color', customColors.headerText);
      }
    }

    // M39: push the resolved websiteId into the api module so product
    // requests scope correctly without reading from window globals.
    try {
      const wid =
        storefrontConfig?.id ??
        storefrontConfig?.storefrontId ??
        storefrontConfig?.websiteId;
      if (typeof wid === 'number' && Number.isFinite(wid)) {
        const { setApiWebsiteId } = await import('../services/api');
        setApiWebsiteId(wid);
      }
      // Tenant scope for /store/products (required by the backend). Use the
      // resolved tenant UUID from the storefront config.
      const tid = (storefrontConfig as any)?.tenantId ?? (tenant as any)?.id;
      if (tid) {
        const { setApiTenantId } = await import('../services/api');
        setApiTenantId(tid);
      }
    } catch {
      // setApiWebsiteId/TenantId are best-effort; missing scope only affects per-website filtering.
    }

    if (config.branding.fontFamily) {
      root.style.setProperty('font-family', config.branding.fontFamily);
    }

    // Update page title — prefer per-website storeName (e.g. "test2") over
    // tenant-level companyName (e.g. "Syntara") so multi-website tenants
    // get the correct tab title. Falls back to tenant when storefront is absent.
    const websiteName = storefrontConfig?.storeName;
    document.title = websiteName || config.branding.companyName;

    // Update favicon. Priority: per-website customFavicon (dedicated tab icon)
    // → per-website customLogo → tenant favicon → tenant logo. This lets each
    // website ship a square tab icon distinct from its header logo.
    const faviconHref =
      storefrontConfig?.customFavicon ||
      storefrontConfig?.customLogo ||
      config.branding.favicon ||
      config.branding.logo;
    if (faviconHref) {
      document.querySelectorAll("link[rel*='icon']").forEach((el) => el.remove());
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = faviconHref;
      if (faviconHref.endsWith('.svg')) link.type = 'image/svg+xml';
      else if (faviconHref.endsWith('.png')) link.type = 'image/png';
      else if (faviconHref.endsWith('.webp')) link.type = 'image/webp';
      document.head.appendChild(link);
    }

    console.debug('[TenantContext] branding applied', config.branding.companyName, { primaryColor, secondaryColor });
  };

  const loadTenantConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      // Preview mode: do NOT short-circuit. Fetch real tenant by domain like
      // live, so branding + products match the customer site. Draft edits from
      // the admin builder arrive via postMessage → `divi:preview-config` event
      // and are merged on top (see effect below).

      // L1: dev-only diagnostics. console.debug is stripped by Vite's
      // drop_console in prod builds, so these vanish from the customer
      // bundle while still being visible during local development.
      console.debug('[TenantContext] loading', {
        hostname: window.location.hostname,
        port: window.location.port,
        buildTenantId: import.meta.env.VITE_TENANT_ID,
        apiUrl: import.meta.env.VITE_API_URL,
        environment: import.meta.env.MODE,
      });

      // PRIORITY 1: Check embedded config completeness (runtime source of truth).
      //
      // Previously this branch read `window.__STOREFRONT_CONFIG__` directly,
      // which gave any injected script on the page a mutable surface to
      // overwrite tenant config (including prices) by the time React rendered.
      // We now route through `getEmbeddedStorefrontConfig()` in
      // `utils/storefrontConfig.ts` — the SSR-embedded payload is read once
      // there with the same completeness check this branch used to do
      // inline. Dev-preview payloads no longer touch window globals at all;
      // they flow through the `divi:preview-config` CustomEvent (see
      // previewBridge.ts) and are observed lower in this component.
      const buildTenantId = import.meta.env.VITE_TENANT_ID;
      const embeddedConfig = getEmbeddedStorefrontConfigSync();
      const hasCompleteEmbedded = !!embeddedConfig;

      console.debug('[TenantContext] config check', { buildTenantId, hasCompleteEmbedded });

      // PRIORITY 2: Development - tenant resolution.
      //
      // Localhost no longer hardcodes a port→tenant table here. Set
      // VITE_DEV_TENANT_ID in your `.env.local` (or VITE_TENANT_ID in any
      // env file) and the API/domain config flow downstream will use it.
      // Maintains the same effective behavior the old port map gave —
      // localhost gets a tenant context — without baking three magic IDs
      // into the bundle.
      const domain = window.location.hostname;
      const port = window.location.port;
      if (import.meta.env.DEV && domain === 'localhost') {
        const devTenantId =
          import.meta.env.VITE_DEV_TENANT_ID ||
          import.meta.env.VITE_TENANT_ID ||
          undefined;
        if (devTenantId && import.meta.env.DEV) {
          console.debug(
            `[TenantContext] localhost:${port} → dev tenant override = ${devTenantId}`,
          );
        }
      }

      // PRIORITY 3: Dynamic loading by domain (Option A deployment)
      console.debug('[TenantContext] fetching tenant config for domain', domain);
      try {
        const config = await tenantAPI.getConfig(domain);
        console.debug('[TenantContext] tenant config loaded', config.name);
        setTenant(config);
        await applyBranding(config);

        // Initialize Facebook Pixel if tenant has configured it
        if ((config as any).analytics?.facebookPixelId) {
          facebookPixel.init((config as any).analytics.facebookPixelId, 'tenant');
          console.debug('[TenantContext] Facebook Pixel initialized', (config as any).analytics.facebookPixelId);

          // Store event settings for conditional tracking
          (window as any).__FB_PIXEL_EVENT_SETTINGS__ = (config as any).analytics?.enabledEvents || {};
        }

        setLoading(false);
        return;
      } catch (apiError: any) {
        console.warn('[TenantContext] tenant config API fetch failed, falling back', apiError.message);
      }

      // FALLBACK: no configuration source — surface as a real error.
      console.warn('[TenantContext] no tenant configuration available');
      throw new Error('No configuration available');
    } catch (err: any) {
      console.error('[TenantContext] failed to load tenant config', {
        error: err.message,
        hostname: window.location.hostname,
        port: window.location.port,
        buildTenantId: import.meta.env.VITE_TENANT_ID,
        stack: import.meta.env.MODE === 'development' ? err.stack : undefined,
      });

      // User-friendly error message
      const errorMessage = import.meta.env.MODE === 'production'
        ? 'Unable to load site configuration. Please try refreshing the page.'
        : `Configuration error: ${err.message}`;

      setError(errorMessage);

      // Production error reporting hook (if Sentry or similar is configured)
      if (import.meta.env.MODE === 'production' && (window as any).Sentry) {
        (window as any).Sentry.captureException(err, {
          tags: {
            context: 'tenant_config_loading',
            hostname: window.location.hostname,
          },
        });
      }

      // HR10: dev-only fallback. The pre-fix code hardcoded three full
      // tenant config blobs (5176→Diviteb id=1, 5177→Nottie id=19,
      // 5178→DomainX id=20) inside this catch block, which baked those
      // tenant ids and brand names into the PRODUCTION bundle. Now the
      // entire fallback is gated on `import.meta.env.DEV` so Vite's
      // dead-code elimination strips it from prod builds. In prod, a
      // failed tenant-load surfaces as the visible Configuration Error
      // panel below — no silent default tenant.
      if (import.meta.env.DEV && window.location.hostname === 'localhost') {
        const devTenantId =
          import.meta.env.VITE_DEV_TENANT_ID ||
          import.meta.env.VITE_TENANT_ID ||
          'localhost';
        const devTenantDomain =
          import.meta.env.VITE_DEV_TENANT_DOMAIN || 'localhost';

        console.debug(
          `[TenantContext] DEV fallback config id=${devTenantId} domain=${devTenantDomain}`,
        );

        const fallbackConfig: TenantConfig = {
          id: devTenantId as any,
          slug: 'localhost',
          name: 'Local Development',
          domain: devTenantDomain,
          type: 'brand',
          status: 'active',
          branding: {
            companyName: 'Local Dev',
            primaryColor: '#2563eb',
            secondaryColor: '#7c3aed',
          },
          homepage: {
            template: 'multi-product',
            sections: {},
          },
          template: {
            customerTemplate: 'multi-product' as any,
            allowedProductTypes: ['normal'] as any,
            enabledPages: {
              home: true,
              products: true,
              menu: true,
              catering: false,
              events: false,
              services: false,
              subscriptions: false,
              about: true,
              contact: true,
            },
            homepage: {
              layout: 'grid',
              sections: [],
            },
            features: {
              enableCart: true,
              enableCheckout: true,
              enableBookings: false,
              enableSubscriptions: false,
              enableAffiliates: false,
              enableWishlist: false,
              enableReviews: false,
              enableLiveChat: false,
            },
            cartBehavior: 'stay',
          },
        };

        setTenant(fallbackConfig);
        await applyBranding(fallbackConfig);
      }
      // (Legacy port-switch fallback removed under HR10. The retired
      // block hardcoded three tenant configs — Diviteb / Nottie /
      // DomainX — and shipped them in the production bundle, baking
      // those tenant ids and brand names into every customer storefront.
      // Anyone who needs a localhost dev override now sets
      // VITE_DEV_TENANT_ID + VITE_DEV_TENANT_DOMAIN in `.env.local`.)
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error && !tenant) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-2">Configuration Error</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Helper functions
  const isProductTypeAllowed = (productType: ProductType): boolean => {
    return checkProductTypeAllowed(tenant, productType);
  };

  const getAllowedProductTypes = (): ProductType[] => {
    return getAllowedTypes(tenant);
  };

  const isPageEnabled = (page: string): boolean => {
    return checkPageEnabled(tenant, page);
  };

  const isFeatureEnabled = (feature: string): boolean => {
    return checkFeatureEnabled(tenant, feature);
  };

  // Development helpers — gated on `import.meta.env.DEV` so Vite's
  // dead-code elimination strips the entire block from the production
  // bundle at build time. window.__TENANT_DEBUG__ is never reachable in
  // a prod build, period.
  if (import.meta.env.DEV) {
    (window as any).__TENANT_DEBUG__ = {
      getTenant: () => {
        console.table({
          'Tenant ID': tenant?.id,
          'Tenant Name': tenant?.name,
          'Domain': tenant?.domain,
          'Primary Color': tenant?.branding?.primaryColor,
          'Template': tenant?.template?.customerTemplate,
        });
        return tenant;
      },
      reloadConfig: () => {
        console.debug('[TenantContext] reloading');
        window.location.reload();
      },
    };

    if (!window.localStorage.getItem('debug_helper_shown')) {
      console.debug(
        '[TenantContext] dev helpers: window.__TENANT_DEBUG__.getTenant() / .reloadConfig()',
      );
      window.localStorage.setItem('debug_helper_shown', 'true');
    }
  }

  return (
    <TenantContext.Provider
      value={{
        tenant,
        loading,
        error,
        isProductTypeAllowed,
        getAllowedProductTypes,
        isPageEnabled,
        isFeatureEnabled,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};
